// @flow
import { all, call, fork, takeEvery, put } from "redux-saga/effects"

import {
  CHANGE_LAYOUT,
  CHANGE_LAYOUT_WIDTH,
  CHANGE_SIDEBAR_THEME,
  CHANGE_SIDEBAR_THEME_IMAGE,
  CHANGE_SIDEBAR_TYPE,
  CHANGE_TOPBAR_THEME,
  SHOW_RIGHT_SIDEBAR,
  CHANGE_LAYOUT_MODE,
  INIT_LAYOUT_FROM_STORAGE
} from "./actionTypes"

import {
  changeSidebarType as changeSidebarTypeAction,
  changeTopbarTheme as changeTopbarThemeAction,
} from "./actions"

/**
 * Saves layout settings to localStorage
 */
function saveSettingsToLocalStorage(settings) {
  try {
    const currentSettings = JSON.parse(localStorage.getItem('layout-settings') || '{}');
    const updatedSettings = { ...currentSettings, ...settings };
    localStorage.setItem('layout-settings', JSON.stringify(updatedSettings));
  } catch (error) {
    console.warn('Error saving layout settings to localStorage:', error);
  }
}

/**
 * Changes the body attribute
 */
function changeBodyAttribute(attribute, value) {
  if (document.body) document.body.setAttribute(attribute, value)
  return true
}

/**
 * Toggle the class on body
 * @param {*} cssClass
 */
function manageBodyClass(cssClass, action = "toggle") {
  switch (action) {
    case "add":
      if (document.body) document.body.classList.add(cssClass)
      break
    case "remove":
      if (document.body) document.body.classList.remove(cssClass)
      break
    default:
      if (document.body) document.body.classList.toggle(cssClass)
      break
  }

  return true
}

/**
 * Changes the layout type
 * @param {*} param0
 */
function* changeLayout({ payload: layout }) {
  try {
    if (layout === "horizontal") {
      yield put(changeTopbarThemeAction("dark"))
      document.body.removeAttribute("data-sidebar")
      document.body.removeAttribute("data-sidebar-image")
      document.body.removeAttribute("data-sidebar-size")
    } else {
      yield put(changeTopbarThemeAction("light"))
    }
    yield call(changeBodyAttribute, "data-layout", layout)
    yield call(saveSettingsToLocalStorage, { layoutType: layout })
  } catch (error) {}
}

/**
 * Changes the layout mode
 * @param {*} param0
 */
 function* changeLayoutMode({ payload: mode }) {
  try {
      yield call(changeBodyAttribute, "data-layout-mode", mode);
      yield call(saveSettingsToLocalStorage, { layoutModeType: mode });
  } catch (error) {
      console.error("Error changing layout mode:", error)
  }
}

/**
 * Changes the layout width
 * @param {*} param0
 */
function* changeLayoutWidth({ payload: width }) {
  try {
    if (width === "boxed") {
      yield put(changeSidebarTypeAction("icon"))
      yield call(changeBodyAttribute, "data-layout-size", width)
      yield call(changeBodyAttribute, "data-layout-scrollable", false)
    } else if (width === "scrollable") {
      yield put(changeSidebarTypeAction("default"))
      yield call(changeBodyAttribute, "data-layout-scrollable", true)
    } else {
      yield put(changeSidebarTypeAction("default"))
      yield call(changeBodyAttribute, "data-layout-size", width)
      yield call(changeBodyAttribute, "data-layout-scrollable", false)
    }
    yield call(saveSettingsToLocalStorage, { layoutWidth: width })
  } catch (error) {}
}

/**
 * Changes the left sidebar theme
 * @param {*} param0
 */
function* changeLeftSidebarTheme({ payload: theme }) {
  try {
    yield call(changeBodyAttribute, "data-sidebar", theme)
    yield call(saveSettingsToLocalStorage, { leftSideBarTheme: theme })
  } catch (error) {}
}

/**
 * Changes the left sidebar theme Image
 * @param {*} param0
 */
 function* changeLeftSidebarThemeImage({ payload: theme }) {
  try {
    yield call(changeBodyAttribute, "data-sidebar-image", theme)
    yield call(saveSettingsToLocalStorage, { leftSideBarThemeImage: theme })
  } catch (error) {}
}

/**
 * Changes the topbar theme
 * @param {*} param0
 */
function* changeTopbarTheme({ payload: theme }) {
  try {
    yield call(changeBodyAttribute, "data-topbar", theme)
    yield call(saveSettingsToLocalStorage, { topbarTheme: theme })
  } catch (error) {}
}

/**
 * Changes the left sidebar type
 * @param {*} param0
 */
function* changeLeftSidebarType({ payload: { sidebarType, isMobile } }) {
  try {
    switch (sidebarType) {
      case "compact":
        yield call(changeBodyAttribute, "data-sidebar-size", "small")
        yield call(manageBodyClass, "sidebar-enable", "remove")
        yield call(manageBodyClass, "vertical-collpsed", "remove")
        break
      case "icon":
        yield call(changeBodyAttribute, "data-sidebar-size", "")
        yield call(changeBodyAttribute, "data-keep-enlarged", "true")
        yield call(manageBodyClass, "vertical-collpsed", "add")
        break
      case "condensed":
        yield call(manageBodyClass, "sidebar-enable", "add")
        if (window.screen.width >= 992) {
          yield call(manageBodyClass, "vertical-collpsed", "remove")
          yield call(manageBodyClass, "sidebar-enable", "remove")
          yield call(manageBodyClass, "vertical-collpsed", "add")
          yield call(manageBodyClass, "sidebar-enable", "add")
        } else {
          yield call(manageBodyClass, "sidebar-enable", "add")
          yield call(manageBodyClass, "vertical-collpsed", "add")
        }
        break
      default:
        yield call(changeBodyAttribute, "data-sidebar-size", "")
        yield call(manageBodyClass, "sidebar-enable", "remove")
        if (!isMobile)
          yield call(manageBodyClass, "vertical-collpsed", "remove")
        break
    }
    yield call(saveSettingsToLocalStorage, { leftSideBarType: sidebarType })
  } catch (error) {}
}

/**
 * Show the rightsidebar
 */
function* showRightSidebar() {
  try {
    yield call(manageBodyClass, "right-bar-enabled", "add")
  } catch (error) {}
}

/**
 * Initialize layout from localStorage
 */
function* initLayoutFromStorage() {
  try {
    const storedSettings = JSON.parse(localStorage.getItem('layout-settings') || '{}');
    
    if (storedSettings.layoutType) {
      yield call(changeBodyAttribute, "data-layout", storedSettings.layoutType);
    }
    
    if (storedSettings.layoutModeType) {
      yield call(changeBodyAttribute, "data-layout-mode", storedSettings.layoutModeType);
    }
    
    if (storedSettings.layoutWidth) {
      if (storedSettings.layoutWidth === "boxed") {
        yield call(changeBodyAttribute, "data-layout-size", storedSettings.layoutWidth);
        yield call(changeBodyAttribute, "data-layout-scrollable", false);
      } else if (storedSettings.layoutWidth === "scrollable") {
        yield call(changeBodyAttribute, "data-layout-scrollable", true);
      } else {
        yield call(changeBodyAttribute, "data-layout-size", storedSettings.layoutWidth);
        yield call(changeBodyAttribute, "data-layout-scrollable", false);
      }
    }
    
    if (storedSettings.leftSideBarTheme) {
      yield call(changeBodyAttribute, "data-sidebar", storedSettings.leftSideBarTheme);
    }
    
    if (storedSettings.leftSideBarThemeImage) {
      yield call(changeBodyAttribute, "data-sidebar-image", storedSettings.leftSideBarThemeImage);
    }
    
    if (storedSettings.topbarTheme) {
      yield call(changeBodyAttribute, "data-topbar", storedSettings.topbarTheme);
    }
    
    if (storedSettings.leftSideBarType) {
      const sidebarType = storedSettings.leftSideBarType;
      switch (sidebarType) {
        case "compact":
          yield call(changeBodyAttribute, "data-sidebar-size", "small");
          yield call(manageBodyClass, "sidebar-enable", "remove");
          yield call(manageBodyClass, "vertical-collpsed", "remove");
          break;
        case "icon":
          yield call(changeBodyAttribute, "data-sidebar-size", "");
          yield call(changeBodyAttribute, "data-keep-enlarged", "true");
          yield call(manageBodyClass, "vertical-collpsed", "add");
          break;
        case "condensed":
          yield call(manageBodyClass, "sidebar-enable", "add");
          if (window.screen.width >= 992) {
            yield call(manageBodyClass, "vertical-collpsed", "remove");
            yield call(manageBodyClass, "sidebar-enable", "remove");
            yield call(manageBodyClass, "vertical-collpsed", "add");
            yield call(manageBodyClass, "sidebar-enable", "add");
          } else {
            yield call(manageBodyClass, "sidebar-enable", "add");
            yield call(manageBodyClass, "vertical-collpsed", "add");
          }
          break;
        default:
          yield call(changeBodyAttribute, "data-sidebar-size", "");
          yield call(manageBodyClass, "sidebar-enable", "remove");
          yield call(manageBodyClass, "vertical-collpsed", "remove");
          break;
      }
    }
    
    // Handle horizontal layout specific settings
    if (storedSettings.layoutType === "horizontal") {
      document.body.removeAttribute("data-sidebar");
      document.body.removeAttribute("data-sidebar-image");
      document.body.removeAttribute("data-sidebar-size");
    }
  } catch (error) {
    console.warn('Error initializing layout from localStorage:', error);
  }
}

/**
 * Watchers
 */
export function* watchChangeLayoutType() {
  yield takeEvery(CHANGE_LAYOUT, changeLayout)
}

export function* watchChangeLayoutWidth() {
  yield takeEvery(CHANGE_LAYOUT_WIDTH, changeLayoutWidth)
}

export function* watchChangeLeftSidebarTheme() {
  yield takeEvery(CHANGE_SIDEBAR_THEME, changeLeftSidebarTheme)
}

export function* watchChangeLeftSidebarThemeImage() {
  yield takeEvery(CHANGE_SIDEBAR_THEME_IMAGE, changeLeftSidebarThemeImage)
}

export function* watchChangeLeftSidebarType() {
  yield takeEvery(CHANGE_SIDEBAR_TYPE, changeLeftSidebarType)
}

export function* watchChangeTopbarTheme() {
  yield takeEvery(CHANGE_TOPBAR_THEME, changeTopbarTheme)
}

export function* watchShowRightSidebar() {
  yield takeEvery(SHOW_RIGHT_SIDEBAR, showRightSidebar)
}

export function* watchSChangeLayoutMode() {
  yield takeEvery(CHANGE_LAYOUT_MODE, changeLayoutMode)
}

export function* watchInitLayoutFromStorage() {
  yield takeEvery(INIT_LAYOUT_FROM_STORAGE, initLayoutFromStorage)
}

function* LayoutSaga() {
  yield all([
    fork(watchSChangeLayoutMode),
    fork(watchChangeLayoutType),
    fork(watchChangeLayoutWidth),
    fork(watchChangeLeftSidebarTheme),
    fork(watchChangeLeftSidebarThemeImage),
    fork(watchChangeLeftSidebarType),
    fork(watchShowRightSidebar),
    fork(watchChangeTopbarTheme),
    fork(watchInitLayoutFromStorage),
  ])
}

export default LayoutSaga
